import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AdminProfileDropdown from "./AdminProfileDropdown.jsx";
import "./AdminLayout.css";

/* ─────────────────────────────────
   Nav structure
   Order: Dashboard → Shop → Content → CRM → Settings
   Groups with children are collapsible; Shop is open by default.
   ───────────────────────────────── */
const NAV = [
  {
    group: "Main",
    icon: "⊞",
    flat: true,          // no collapsible header — renders as a plain link row
    items: [
      { icon: "⊞", label: "Dashboard", path: "/admin/dashboard" },
    ],
  },
  {
    group: "Shop",
    icon: "🛒",
    items: [
      { icon: "☰", label: "Shop Products",   path: "/admin/shop/products",       dot: true },
      { icon: "⊟", label: "Categories",      path: "/admin/shop/categories" },
      { icon: "🗒", label: "Orders",          path: "/admin/shop/orders",         dot: true },
      { icon: "✂", label: "Coupons",         path: "/admin/shop/coupons" },
      { icon: "₿", label: "Banking Details", path: "/admin/shop/banking-details" },
    ],
  },
  {
    group: "Content",
    icon: "◧",
    items: [
      { icon: "◫", label: "Slideshow", path: "/admin/content/slideshow" },
      { icon: "◧", label: "Services",  path: "/admin/content/services" },
      { icon: "◨", label: "Products",  path: "/admin/content/products" },
      { icon: "❓", label: "FAQ",       path: "/admin/content/faq" },
      { icon: "◉", label: "Gallery",   path: "/admin/content/gallery" },
      { icon: "▷", label: "Videos",    path: "/admin/content/videos" },
      { icon: "✦", label: "Navbar",    path: "/admin/content/navbar" },
    ],
  },
  {
    group: "CRM",
    icon: "♟",
    items: [
      { icon: "♟", label: "Customers",    path: "/admin/crm/customers" },
      { icon: "✉", label: "Wishlist",     path: "/admin/crm/wishlist",     dot: true },
      { icon: "◈", label: "Quotations",   path: "/admin/crm/quotations" },
      { icon: "★", label: "Solutions Steps", path: "/admin/crm/testimonials" },
    ],
  },
  {
    group: "Settings",
    icon: "⚙",
    items: [
      { icon: "☎", label: "Company Contact", path: "/admin/settings/company-contact" },
      { icon: "✪", label: "About Us",        path: "/admin/settings/about" },
      { icon: "⚙", label: "Team Members",    path: "/admin/settings/team" },
      { icon: "⊕", label: "Social Links",    path: "/admin/settings/social" },
      { icon: "⊗", label: "Logos",           path: "/admin/settings/logos" },
      { icon: "⊛", label: "Users",           path: "/admin/settings/users" },
    ],
  },
];

/* ─────────────────────────────────
   Logout Confirmation Modal
   (used by the sidebar logout button)
   ───────────────────────────────── */
function LogoutModal({ onConfirm, onCancel }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel]);

  return (
    <div className="al-modal-overlay" onClick={onCancel}>
      <div className="al-modal" onClick={(e) => e.stopPropagation()}>
        <div className="al-modal__icon">⏻</div>
        <h2 className="al-modal__title">Sign Out?</h2>
        <p className="al-modal__body">
          You are about to sign out of the admin panel.<br />
          Any unsaved changes will be lost.
        </p>
        <div className="al-modal__actions">
          <button className="al-modal__btn al-modal__btn--cancel" onClick={onCancel}>
            Cancel
          </button>
          <button className="al-modal__btn al-modal__btn--confirm" onClick={onConfirm}>
            Yes, Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────
   Collapsible nav group
   ───────────────────────────────── */
function NavGroup({ group, isOpen, onToggle, collapsed, location, navigate }) {
  const hasActiveChild = group.items.some((i) => location.pathname === i.path);

  /* Flat groups (Dashboard) render a single plain button, no header toggle */
  if (group.flat) {
    const item = group.items[0];
    const active = location.pathname === item.path;
    return (
      <div className="as-nav__group">
        <button
          className={`as-nav__item as-nav__item--flat${active ? " as-nav__item--active" : ""}`}
          onClick={() => navigate(item.path)}
          title={collapsed ? item.label : undefined}
        >
          <span className="as-nav__icon">{item.icon}</span>
          {!collapsed && <span className="as-nav__label">{item.label}</span>}
        </button>
      </div>
    );
  }

  return (
    <div className={`as-nav__group as-nav__group--collapsible${isOpen ? " as-nav__group--open" : ""}`}>
      {/* Group header / toggle */}
      <button
        className={`as-nav__group-header${hasActiveChild ? " as-nav__group-header--active" : ""}${isOpen ? " as-nav__group-header--open" : ""}`}
        onClick={onToggle}
        title={collapsed ? group.group : undefined}
      >
        <span className="as-nav__group-icon">{group.icon}</span>
        {!collapsed && (
          <>
            <span className="as-nav__group-label">{group.group}</span>
            <span className="as-nav__group-chevron">{isOpen ? "▾" : "›"}</span>
          </>
        )}
        {/* Active indicator dot when collapsed */}
        {collapsed && hasActiveChild && <span className="as-nav__group-dot" />}
      </button>

      {/* Children — animate open/close */}
      {!collapsed && (
        <div
          className="as-nav__children"
          style={{ maxHeight: isOpen ? `${group.items.length * 44}px` : "0" }}
        >
          {group.items.map((item) => {
            const active = location.pathname === item.path;
            return (
              <button
                key={item.path}
                className={`as-nav__item as-nav__item--child${active ? " as-nav__item--active" : ""}`}
                onClick={() => navigate(item.path)}
              >
                <span className="as-nav__icon">{item.icon}</span>
                <span className="as-nav__label">{item.label}</span>
                {item.dot && <span className="as-nav__dot" />}
              </button>
            );
          })}
        </div>
      )}

      {/* When collapsed: show children in a tooltip-style flyout */}
      {collapsed && (
        <div className="as-nav__flyout">
          <div className="as-nav__flyout-title">{group.group}</div>
          {group.items.map((item) => {
            const active = location.pathname === item.path;
            return (
              <button
                key={item.path}
                className={`as-nav__flyout-item${active ? " as-nav__flyout-item--active" : ""}`}
                onClick={() => navigate(item.path)}
              >
                <span>{item.icon}</span>
                <span>{item.label}</span>
                {item.dot && <span className="as-nav__dot" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────
   AdminLayout
   ───────────────────────────────── */
export default function AdminLayout({ children, pageTag, pageTitle }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed,       setCollapsed]       = useState(false);
  const [mobileOpen,      setMobileOpen]      = useState(false);
  const [now,             setNow]             = useState(new Date());
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  /* Track which group is open — only one at a time; Shop open by default */
  const [openGroup, setOpenGroup] = useState("Shop");

  const user = (() => {
    try { return JSON.parse(localStorage.getItem("admin_user")) || {}; }
    catch { return {}; }
  })();

  /* Live clock */
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  /* Close mobile drawer on route change */
  useEffect(() => setMobileOpen(false), [location.pathname]);

  /* When the active route changes, ensure the parent group is open */
  useEffect(() => {
    for (const group of NAV) {
      if (group.flat) continue;
      if (group.items.some((i) => location.pathname === i.path)) {
        setOpenGroup(group.group);
        break;
      }
    }
  }, [location.pathname]);

  const handleToggleGroup = (groupName) => {
    setOpenGroup((prev) => (prev === groupName ? null : groupName));
  };

  /* Logout */
  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin/login");
  };

  const dateStr = now.toLocaleDateString("en-ZA", {
    weekday: "short", day: "numeric", month: "short", year: "numeric",
  });

  return (
    <div className={`ad-root${collapsed ? " ad-root--collapsed" : ""}`}>

      {/* ── Logout Confirmation Modal (sidebar button) ── */}
      {showLogoutModal && (
        <LogoutModal
          onConfirm={handleLogout}
          onCancel={() => setShowLogoutModal(false)}
        />
      )}

      {/* ── Sidebar (dark) ── */}
      <aside className={`as-sidebar${collapsed ? " as-sidebar--collapsed" : ""}${mobileOpen ? " as-sidebar--open" : ""}`}>

        {/* Logo row */}
        <div className="as-logo">
          <span className="as-logo__icon">⚙</span>
          {!collapsed && <span className="as-logo__text">NoelAuto</span>}
          <button
            className="as-collapse-btn"
            onClick={() => setCollapsed((v) => !v)}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? "›" : "‹"}
          </button>
        </div>

        {/* Nav */}
        <nav className="as-nav">
          {NAV.map((group) => (
            <NavGroup
              key={group.group}
              group={group}
              isOpen={openGroup === group.group}
              onToggle={() => handleToggleGroup(group.group)}
              collapsed={collapsed}
              location={location}
              navigate={navigate}
            />
          ))}
        </nav>

        {/* Logout — red, opens confirm modal */}
        <button
          className="as-logout as-logout--red"
          onClick={() => setShowLogoutModal(true)}
          title="Logout"
        >
          <span style={{ fontSize: 16 }}>⏻</span>
          {!collapsed && <span>Logout</span>}
        </button>
      </aside>

      {/* ── Main area ── */}
      <div className="ad-main">

        {/* Topbar */}
        <header className="at-topbar">
          <div className="at-topbar__left">
            <button
              className="at-topbar__hamburger"
              onClick={() => setMobileOpen((v) => !v)}
            >
              ☰
            </button>

            {pageTag && <div className="at-topbar__tag">{pageTag}</div>}
            {pageTitle && <h1 className="at-topbar__title">{pageTitle}</h1>}
          </div>

          <div className="at-topbar__right">
            <span className="at-topbar__date">{dateStr}</span>
            <AdminProfileDropdown user={user} onLogout={handleLogout} />
          </div>
        </header>

        {/* Page content */}
        <main className="ad-content">
          {children}
        </main>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,.5)",
            zIndex: 99,
          }}
        />
      )}
    </div>
  );
}